/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// React
import React from "react";
// redux hooks
import { useFieldArray, Controller } from "react-hook-form";
// BASE components
import BASESvgIcon from '../../../../../../@BASE/core/BASESvgIcon';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from "@mui/system/Box";
import Button from "@mui/material/Button"
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from "@mui/material/Typography";
// custom components
import NumberedHeading from "../../../../../shared-components/forms/NumberedHeading";
import WYSIWYGEditor from "../../../../../shared-components/WYSIWYGEditor";
import { fi } from "date-fns/locale";
import MediaQuestionsLureArray from "./MediaQuestionsLureArray";

export default function MediaQuestionsArray({ control, register, setValue, getValues, errors, inputDir, watch }) {
  // setup field array
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'content.questions',
  });

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" sx={{marginTop: '22px'}} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="py-0">
                <Accordion
                  defaultExpanded
                  className="bg-transparent shadow-none"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography className="grow m-0 font-semibold">
                      Question {index + 1}
                    </Typography>
                    {index !== 0 &&
                      <IconButton 
                        className="ml-16 mr-8"
                        aria-label="move up"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index - 1);
                        }}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>
                      </IconButton>
                    }
                    {index !== fields.length - 1 &&
                      <IconButton 
                        className="mx-8"
                        aria-label="move down"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index + 1);
                        }}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>
                      </IconButton>
                    }
                    <IconButton
                      className="ml-8 mr-16"
                      aria-label="delete"
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    >
                      <BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>
                    </IconButton>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box
                      className="p-0"
                    >
                      <NumberedHeading number="1" title="Start Time (Minute)" instructions="Enter a start time (minute) for the focus point" />
                      <Controller
                        control={control}
                        name={`content.questions.${index}.start_minute`}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="mt-16 mb-16"
                            label="Start Time (Minute)"
                            placeholder="Start Time (Minute)"
                            error={!!errors.content?.questions?.[index]?.start_minute}
                            helperText={errors.content?.questions?.[index]?.start_minute?.message}
                            fullWidth
                            required
                            type="number"
                          />
                        )}
                      /> 

                      <NumberedHeading number="2" title="Start Time (Second)" instructions="Enter a start time (second) for the focus point" />
                      <Controller
                        control={control}
                        name={`content.questions.${index}.start_second`}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="mt-16 mb-16"
                            label="Start Time (Second)"
                            placeholder="Start Time (Second)"
                            error={!!errors.content?.questions?.[index]?.start_second}
                            helperText={errors.content?.questions?.[index]?.start_second?.message}
                            fullWidth
                            required
                            type="number"
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading number="3" title="End Time (Minute)" instructions="Enter an end time (minute) for the focus point" />
                      <Controller
                        control={control}
                        name={`content.questions.${index}.end_minute`}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="mt-16 mb-16"
                            label="End Time (Minute)"
                            placeholder="End Time (Minute)"
                            error={!!errors.content?.questions?.[index]?.end_minute}
                            helperText={errors.content?.questions?.[index]?.end_minute?.message}
                            fullWidth
                            required
                            type="number"
                          />
                        )}
                      /> 

                      <NumberedHeading number="4" title="End Time (Second)" instructions="Enter a end time (second) for the focus point" />
                      <Controller
                        control={control}
                        name={`content.questions.${index}.end_second`}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="mt-16 mb-16"
                            label="End Time (Second)"
                            placeholder="End Time (Second)"
                            error={!!errors.content?.questions?.[index]?.end_second}
                            helperText={errors.content?.questions?.[index]?.end_second?.message}
                            fullWidth
                            required
                            type="number"
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading number="5" title="Title" instructions="Write title of this Focus Point" />
                      <Controller
                        control={control}
                        name={`content.questions.${index}.title`}
                        render={({ field }) => (
                          <TextField
                            className="mt-16"
                            {...field}
                            label="Title"
                            placeholder="Title"
                            id="title"
                            error={!!errors.content?.questions?.[index]?.title}
                            helperText={errors.content?.questions?.[index]?.title?.message}
                            variant="outlined"  
                            fullWidth
                            required
                            dir={inputDir}
                          />
                        )}
                      />
                          
                      <Divider className="mt-16" />

                      <NumberedHeading number="6" title="Question Type" instructions="Select the type of question." />
                      <Controller
                        control={control}
                        name={`content.questions.${index}.template_id`}
                        render={({ field }) => (
                          <Controller
                            control={control}
                            name={`content.questions.${index}.template_id`}
                            render={({ field }) => (
                              <FormControl fullWidth className="mt-16" error={!!errors.content?.questions?.[index]?.template_id}>
                                <Select
                                  {...field}
                                  id="template_id"
                                  classes={{ select: "flex items-center space-x-12" }}
                                  displayEmpty
                                  inputProps={{ "aria-label": "Question Type" }}
                                  required
                                >
                                  <MenuItem value="" disabled className="space-x-12">
                                    Select a question type
                                  </MenuItem>
                                  <MenuItem value={0} className="space-x-12">
                                    Audio Statement Completion
                                  </MenuItem>
                                  <MenuItem value={1} className="space-x-12">
                                    Audio True/False
                                  </MenuItem>
                                  <MenuItem value={2} className="space-x-12">
                                    Audio Multiple Choice
                                  </MenuItem>
                                  <MenuItem value={3} className="space-x-12">
                                    Focus Point Check
                                  </MenuItem>
                                </Select>
                                {!!errors.content?.questions?.[index]?.template_id && (
                                  <FormHelperText>{errors.content?.questions?.[index]?.template_id?.message}</FormHelperText>
                                )}
                              </FormControl>
                            )}
                          />
                        )}
                      />

                      {watch(`content.questions.${index}.template_id`) === "" ? (
                        <div className="w-100 mx-32 my-96">
                          <div className="flex items-center justify-center">
                            <BASESvgIcon size={24}>heroicons-outline:exclamation</BASESvgIcon>
                            <Typography className="text-16 p-16">
                              Please select a question type.
                            </Typography>
                          </div>
                        </div>
                        

                      ) : (

                        <>
                        <Divider className="mt-16" />

                        <NumberedHeading number="7" title="Prompt" instructions="Write the prompt for this question." />
                        <Controller
                          control={control}
                          name={`content.questions.${index}.content.prompt`}
                          render={({ field }) => (
                            <WYSIWYGEditor {...field} error={errors.content?.questions?.[index]?.content?.prompt} />
                          )}
                        />

                        <Divider className="mt-16" />

                        <NumberedHeading number="8" title="Prompt Audio" instructions="Select whether or not the prompt should be turned into audio." />
                        <Controller
                          control={control}
                          name={`content.questions.${index}.content.prompt_audio`}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  onChange={(ev) => {
                                    field.onChange(ev.target.checked);
                                  }}
                                  checked={field.value} 
                                />
                              }
                              label="Prompt Audio"
                            />
                          )}
                        />

                        {watch(`content.questions.${index}.template_id`) !== 3 ? (
                          <>
                            <Divider className="mt-16" />

                            <NumberedHeading number="9" title="Lures" instructions="Using the controls below, please add lures for the question." />
                            <Controller
                              control={control}
                              name={`content.questions.${index}.content.lures`}
                              render={({ field }) => (
                                <MediaQuestionsLureArray nestIndex={index} inputDir={inputDir} {...{control, errors }} />
                              )}
                            />

                            <Divider className="mt-16" />

                            <NumberedHeading number="10" title="Correct Feedback" instructions="Enter the correct feedback." />
                            <Controller
                              control={control}
                              name={`content.questions.${index}.content.correct_feedback`}
                              render={({ field }) => (
                                <WYSIWYGEditor {...field} error={errors.content?.questions?.[index]?.content?.correct_feedback} />
                              )}
                            />

                            <Divider className="mt-16" />

                            <NumberedHeading number="11" title="Incorrect Feedback" instructions="Enter the incorrect feedback." />
                            <Controller
                              control={control}
                              name={`content.questions.${index}.content.incorrect_feedback`}
                              render={({ field }) => (
                                <WYSIWYGEditor {...field} error={errors.content?.questions?.[index]?.content?.incorrect_feedback} />
                              )}
                            />
                          </>
                        ) : (
                          <>
                            <Divider className="mt-16" />

                            <NumberedHeading number="9" title="Suggested Feedback" instructions="Enter the suggested feedback." />
                            <Controller
                              control={control}
                              name={`content.questions.${index}.content.suggested_feedback`}
                              render={({ field }) => (
                                <WYSIWYGEditor {...field} error={errors.content?.questions?.[index]?.content?.suggested_feedback} />
                              )}
                            />

                            <Divider className="mt-16" />

                            <NumberedHeading number="10" title="Focus Point" instructions="Enter the content of the focus point." />
                            <Controller
                              control={control}
                              name={`content.questions.${index}.content.focus_point`}
                              render={({ field }) => (
                                <WYSIWYGEditor {...field} error={errors.content?.questions?.[index]?.content?.focus_point} />
                              )}
                            />
                          </>
                        )}
                        </>
                      )}
                      <Divider className="mt-16" />
                      
                    </Box>
                  </AccordionDetails>
                  <AccordionActions>
                    {index !== 0 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index - 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>}
                        size="small"
                      >
                        Move Up
                      </Button>
                    }
                    {index !== fields.length - 1 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index + 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>}
                        size="small"
                      >
                        Move Down
                      </Button>
                    }
                    <Button
                      variant="contained"
                      onClick={() => remove(index)}
                      color="error"
                      startIcon={<BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>}
                      size="small"
                    >
                      Remove
                    </Button>
                  </AccordionActions>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-8 rounded cursor-pointer"
          color="secondary"
          variant="contained"
          onClick={() => append({ 
            start_minute: 0,
            start_second: 0,
            end_minute: 0,
            end_second: 0,
            title: "",
            template_id: "",
            content: {
              prompt: "",
              prompt_audio: false,
              lures: [],
              correct_feedback: "",
              incorrect_feedback: "",
              suggested_feedback: "",
              focus_point: "",
            }
          })}
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add Question
          </span>
        </Button>
      </Box>
    </>
  );
}
